/**
 * Normalised error shape used across the app.
 *
 * `toAppError(e)` converts whatever shape an underlying library threw
 * (PostgrestError, Error, DOMException, plain string, fetch rejection, etc.)
 * into a single predictable `{ message, code, cause }` object.
 *
 * Wire it into the UI once — toasts, banners, query error branches — and the
 * patchwork of `err?.message ?? 'Something went wrong'` disappears. When
 * Sentry / PostHog are wired in, a single `reportError(appErr)` call captures
 * everything with a consistent `code` tag for filtering.
 *
 * ──────────────────────────────────────────────────────────────────────
 * Error taxonomy (ADR 0003)
 *
 *   ErrorCode           — the named catalogue of app-level codes. String-
 *                         literal union; backward-compatible with the
 *                         existing slash-delimited codes so `toAppError`
 *                         output keeps its stable shape.
 *   UIError             — developer-declared user-facing error. `message`
 *                         is always safe to render in a toast or banner.
 *   NeverError          — "this should be impossible." Defensive assertion
 *                         surface — thrown when an exhaustive switch or
 *                         invariant fails.
 *   LinkedInError       — upstream LinkedIn/Voyager API surfaced the error.
 *   FeatureDeniedError  — tier-gate / feature-flag refused the action.
 *   SchemaMismatchError — persisted data failed a Zod / shape check at the
 *                         boundary.
 *   CooldownError       — rate-limit backoff is active; try later.
 *   Result<T>           — discriminated-union return shape (`ok: true|false`).
 *                         Precondition for the mutation registry in ADR 0013;
 *                         usable standalone today.
 * ──────────────────────────────────────────────────────────────────────
 */

export interface AppError {
  /** Human-readable message suitable for toasts. Always present. */
  message: string
  /**
   * Stable machine-readable code. Used to branch UI copy (e.g. `auth/*`,
   * `network/*`, `supabase/<pg_code>`, `extension/*`). Absent when we
   * couldn't confidently classify the error.
   */
  code?: string
  /** Original error object, retained for logger / Sentry. */
  cause?: unknown
}

// Recognised error shapes, narrowed to just the fields we read.
interface PostgrestLikeError {
  message: string
  code?: string
  details?: string
  hint?: string
}

interface AuthLikeError {
  message: string
  status?: number
  name?: string
}

function hasField<K extends string>(x: unknown, key: K): x is Record<K, unknown> {
  return typeof x === 'object' && x !== null && key in x
}

function isPostgrestError(e: unknown): e is PostgrestLikeError {
  // Supabase's PostgrestError has string `code` like '23505', '42501', 'PGRST116'.
  if (!hasField(e, 'message') || !hasField(e, 'code')) return false
  const { code, message } = e as { code: unknown; message: unknown }
  if (typeof code !== 'string' || typeof message !== 'string') return false
  // Postgres SQLSTATE codes are 5 chars; PostgREST codes start with 'PGRST' +
  // up to 3 digits (PGRST116, PGRST301, …). Anything else fails the guard.
  return code.length <= 6 || code.startsWith('PGRST')
}

function isAuthError(e: unknown): e is AuthLikeError {
  // Supabase's AuthError shape. `status` is HTTP-ish, `name` is 'AuthApiError' etc.
  if (!hasField(e, 'name') || !hasField(e, 'message')) return false
  const { name, message } = e as { name: unknown; message: unknown }
  if (typeof name !== 'string' || typeof message !== 'string') return false
  return name.startsWith('Auth')
}

function isAbortError(e: unknown): boolean {
  if (e instanceof DOMException && e.name === 'AbortError') return true
  if (hasField(e, 'name')) {
    const { name } = e as { name: unknown }
    if (typeof name === 'string' && name === 'AbortError') return true
  }
  return false
}

/**
 * Convert any thrown value into an AppError. Never throws; always returns.
 *
 * Classification priority:
 *   1. Abort → `app/aborted` (silent — caller decides whether to surface)
 *   2. Supabase PostgrestError → `supabase/<pg_code>`
 *   3. Supabase AuthError → `auth/<status>`
 *   4. Native Error → message + `app/generic`
 *   5. String → message + `app/generic`
 *   6. Anything else → `app/unknown`
 */
export function toAppError(e: unknown): AppError {
  if (e instanceof AppErrorImpl) return e

  if (isAbortError(e)) {
    return { message: 'Cancelled', code: 'app/aborted', cause: e }
  }

  if (isPostgrestError(e)) {
    return {
      message: e.message || 'Database error',
      code: `supabase/${e.code ?? 'unknown'}`,
      cause: e,
    }
  }

  if (isAuthError(e)) {
    const status = hasField(e, 'status') ? (e as { status: unknown }).status : undefined
    return {
      message: e.message || 'Authentication error',
      code: `auth/${typeof status === 'number' ? status : 'unknown'}`,
      cause: e,
    }
  }

  if (e instanceof Error) {
    return { message: e.message || 'Unexpected error', code: 'app/generic', cause: e }
  }

  if (typeof e === 'string') {
    return { message: e, code: 'app/generic' }
  }

  return { message: 'Unexpected error', code: 'app/unknown', cause: e }
}

/**
 * Marker subclass so repeated `toAppError()` calls are idempotent.
 * You rarely need to construct this directly — prefer `toAppError(err)` or
 * `throwAppError()`. Exported so test assertions + `instanceof` checks in
 * other libraries can recognise app-level errors without duck-typing.
 *
 * Extends `Error` so:
 *   - `e instanceof Error` is `true`, which is what the logger, Sentry, and
 *     most third-party error collectors check before capturing.
 *   - Stack traces are populated automatically.
 *   - Devtools render the error in the stack-frame view, not as a plain
 *     object.
 */
export class AppErrorImpl extends Error implements AppError {
  code?: string
  override cause?: unknown
  constructor(init: AppError) {
    super(init.message)
    this.name = 'AppErrorImpl'
    this.code = init.code
    if (init.cause !== undefined) this.cause = init.cause
  }
}

/**
 * Convenience — throw an AppError with a stable code. Useful inside TQ
 * queryFn / mutationFn so `toAppError` upstream keeps the code intact.
 */
export function throwAppError(message: string, code?: string, cause?: unknown): never {
  throw new AppErrorImpl({ message, code, cause })
}

/** True when the error is a silent-cancel (AbortController, navigation, etc.). */
export function isAborted(e: unknown): boolean {
  if (e instanceof AppErrorImpl) return e.code === ErrorCode.ABORTED
  return isAbortError(e)
}

// ─── Named error-code catalogue ─────────────────────────────────────────────
//
// The values are the same slash-delimited strings `toAppError` already emits
// and UI code already branches on. Having them in a named constant object
// gives us:
//   - a single place to find / rename / deprecate codes
//   - autocomplete at call sites
//   - exhaustiveness checks (ErrorCode union) when switching on codes
//
// The Supabase and Auth prefixes stay dynamic (`supabase/<pg_code>`,
// `auth/<status>`) because those classifiers emit a long tail — enumerating
// them would add no safety and regular drift.

export const ErrorCode = {
  // Control-flow / lifecycle
  ABORTED:           'app/aborted',
  GENERIC:           'app/generic',
  UNKNOWN:           'app/unknown',

  // App-level invariants and flags
  NEVER:             'app/never',
  UI_ERROR:          'app/ui',
  FEATURE_DENIED:    'app/feature-denied',
  SCHEMA_MISMATCH:   'app/schema-mismatch',
  COOLDOWN:          'app/cooldown',

  // LinkedIn + extension
  LINKEDIN_ERROR:    'linkedin/error',
  PROFILE_MISMATCH:  'linkedin/profile-mismatch',
  PROFILE_MISSING:   'linkedin/profile-missing',
  EXTENSION_MISSING: 'extension/missing',
  EXTENSION_STALE:   'extension/stale',

  // Data layer (post-persistence)
  DATA_MISSING:      'data/missing',
  SUSPENDED:         'data/suspended',
} as const

export type ErrorCode = typeof ErrorCode[keyof typeof ErrorCode]

// ─── Named error classes (Kondo-style factory, adapted) ─────────────────────
//
// Each class is a thin subclass of `AppErrorImpl`. Throw them wherever you
// used to `throw new Error('...')` — `toAppError` recognises them (because
// they `instanceof AppErrorImpl`), so downstream normalisation still works.
//
// `userFacing` is `false` by default. Only `UIError` flips it to `true`.
// Filter on `isUserFacing(e)` when you need to decide between "toast the
// message" vs "log + generic UI".

class CodedError extends AppErrorImpl {
  readonly userFacing: boolean
  /**
   * `name` is set so `error.stack` and devtools surfaces make it obvious
   * which subclass was thrown (otherwise every subclass prints as
   * `AppErrorImpl` in a stack frame).
   */
  constructor(
    name: string,
    code: ErrorCode,
    message: string,
    userFacing: boolean,
    cause?: unknown,
  ) {
    super({ message, code, cause })
    this.name = name
    this.userFacing = userFacing
  }
}

/**
 * The message on this error is guaranteed safe to display to the user. Use
 * when you need to tell the user *specifically* what went wrong — not the
 * fallback "Something went wrong."
 *
 *   throw new UIError('That email address is already on a team.')
 */
export class UIError extends CodedError {
  constructor(message: string, cause?: unknown) {
    super('UIError', ErrorCode.UI_ERROR, message, /*userFacing:*/ true, cause)
  }
}

/**
 * Invariant violated — this branch should have been impossible. Use to make
 * unreachable code *actually* unreachable with a thrown assertion rather
 * than a bare `throw new Error('never')`.
 *
 *   switch (kind) {
 *     case 'a': return ...
 *     case 'b': return ...
 *     default: throw new NeverError(`unhandled kind: ${kind}`)
 *   }
 */
export class NeverError extends CodedError {
  constructor(message: string, cause?: unknown) {
    super('NeverError', ErrorCode.NEVER, message, false, cause)
  }
}

/**
 * Something in LinkedIn / Voyager / the extension's LinkedIn bridge failed.
 * Doesn't carry status-code information by itself — wrap the original in
 * `cause` if upstream had one.
 */
export class LinkedInError extends CodedError {
  constructor(message: string, cause?: unknown) {
    super('LinkedInError', ErrorCode.LINKEDIN_ERROR, message, false, cause)
  }
}

/**
 * Tier-gate / feature-flag refused the action. Thrown by feature-flag
 * plumbing; caught to show an upgrade / "not available on your plan" UI.
 */
export class FeatureDeniedError extends CodedError {
  constructor(message: string, cause?: unknown) {
    super('FeatureDeniedError', ErrorCode.FEATURE_DENIED, message, false, cause)
  }
}

/**
 * Persisted data failed a Zod / shape check at the boundary (preferences,
 * sidebar config, Dexie row parse, postMessage payload). Indicates either
 * a migration we missed or upstream shape drift.
 */
export class SchemaMismatchError extends CodedError {
  constructor(message: string, cause?: unknown) {
    super('SchemaMismatchError', ErrorCode.SCHEMA_MISMATCH, message, false, cause)
  }
}

/**
 * Rate-limit / cooling-period active. The caller should back off; UI can
 * surface "too many requests, try again in a moment."
 */
export class CooldownError extends CodedError {
  constructor(message: string, cause?: unknown) {
    super('CooldownError', ErrorCode.COOLDOWN, message, false, cause)
  }
}

// ─── User-facing classification ─────────────────────────────────────────────

/**
 * True when the error's `message` is safe to render verbatim in user-visible
 * UI. Only `UIError` sets this explicitly; everything else is treated as
 * developer-facing and should be collapsed into a generic message at the
 * presentation layer.
 */
export function isUserFacing(e: unknown): boolean {
  if (e instanceof CodedError) return e.userFacing
  return false
}

// ─── Result<T> discriminated union ──────────────────────────────────────────
//
// Used wherever a call may fail in a structured, recoverable way and the
// caller should *handle* the failure rather than let it propagate. Precedes
// and anchors the mutation registry in ADR 0013 — every named mutation will
// return `Promise<Result<T>>`. Outside of the registry, use `Result` when
// you specifically want callers to pattern-match `ok: true | false` rather
// than wrap with try/catch.

export type Result<T, E = AppError> =
  | { ok: true; data: T }
  | { ok: false; error: E }

/** Construct a success result. */
export function ok<T>(data: T): Result<T> {
  return { ok: true, data }
}

/**
 * Construct a failure result. `err` is chosen over `error`/`fail` because
 * it pairs symmetrically with `ok` at call sites.
 */
export function err<T = never>(error: AppError): Result<T> {
  return { ok: false, error }
}

/**
 * Run `fn` (sync or async) and return a Result. Any thrown value is funneled
 * through `toAppError` so callers get the normalized shape without having
 * to wrap try/catch themselves.
 *
 * Abort errors are passed through as-is (`ok: false, error.code === 'app/aborted'`)
 * — the caller decides whether "cancelled" is a real failure in their
 * context or a silent no-op.
 */
export async function asResult<T>(
  fn: () => T | Promise<T>,
): Promise<Result<T>> {
  try {
    return ok(await fn())
  } catch (e) {
    return err(toAppError(e))
  }
}
